import { UploadElementType } from "@/components/common/point-cloud-file-upload-context/use-upload-element-types";
import { selectAllBackgroundTasks } from "@/store/background-tasks/background-tasks-selector";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  selectLightweightElement,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import {
  LightweightElementsStore,
  type LightweightElementsState,
} from "@/store/lightweight-elements-slice";
import {
  BackgroundTask,
  isAssetDetectionTask,
  isCaptur3dFloorplanGenerationTask,
  isCreateProjectSnapshotTask,
  isFileUploadTask,
  isGenericRegistrationTask,
  isMultiFileUploadTask,
  isOrthophotoGenerationTask,
  isPointCloudExportTask,
  isProcessingTask,
  isReportGenerationTask,
  isSceneConversionTask,
  isTiledImageSheetGenerationTask,
  isVlmIndexMigrationTask,
} from "@/utils/background-tasks";
import { compareDateTimes } from "@faro-lotv/foundation";
import {
  GUID,
  ISOTimeString,
  isGeoReferencedElement,
  isIElementBimModelSection,
  isIElementGenericStream,
  isIElementSectionDataSession,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectChildDepthFirst,
  selectIsElementAligned,
  selectLoadedIElements,
} from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";

/** The source of information about a process in progress */
export type ProcessInfoSource = {
  /** Id of the source, can be a Section ID or a BackgroundTask ID */
  id: GUID;

  /** The creation of the process (either of the task or the iElement) */
  createdAt: ISOTimeString;

  /** State of the task */
  state: BackgroundTaskState;

  /** True if this is an ElementToAlignSection, false if it's a BackgroundTask */
  isElementToAlign?: boolean;
};

/**
 * @returns true if the task is related to a lightweight element which is currently present in the project
 * @param task task to be checked for validity related to an iElement
 * @param state root state
 */
function isValidTaskWithLightweightElement(
  task: BackgroundTask,
  state: LightweightElementsStore,
): boolean {
  const lightweightElement = task.iElementId
    ? selectLightweightElement(task.iElementId)(state)
    : undefined;
  return !!lightweightElement;
}

/**
 * Memoized selector that computes the list of processes to show in the progress overview menu
 *
 * @returns the list of processes to show in the progress overview menu
 */
export const selectProcesses = createSelector(
  [
    selectIElementsSlice,
    selectLightweightElementsSlice,
    selectAllBackgroundTasks,
  ],
  computeProcesses,
);

/**
 * @param iElements the iElements slice of the store
 * @param lightweightElements the lightweight elements slice of the store
 * @param backgroundTasks the background tasks slice of the store
 * @returns the list of processes to show in the progress overview menu
 */
function computeProcesses(
  iElements: IElementsState,
  lightweightElements: LightweightElementsState,
  backgroundTasks: BackgroundTask[],
): ProcessInfoSource[] {
  const iElementsState = { iElements };
  const processes: ProcessInfoSource[] = [];

  const unalignedSections = selectLoadedIElements(iElementsState).filter(
    (el) =>
      !isGeoReferencedElement(el) &&
      !selectIsElementAligned(el)(iElementsState) &&
      (isIElementSectionDataSession(el) || isIElementBimModelSection(el)) &&
      !!selectChildDepthFirst(el, isIElementGenericStream)(iElementsState),
  );

  processes.push(
    ...unalignedSections.map((section) => ({
      id: section.id,
      createdAt: section.createdAt,
      state: BackgroundTaskState.scheduled,
      isElementToAlign: true,
    })),
  );

  const visibleTasks = backgroundTasks.filter(
    (task) =>
      !task.isDismissed && shouldShowTask(task, { lightweightElements }),
  );

  processes.push(
    ...visibleTasks.map((task) => ({
      id: task.id,
      createdAt: task.createdAt,
      state: task.state,
    })),
  );

  return processes.sort((a, b) => compareDateTimes(b.createdAt, a.createdAt));
}

/**
 * @param task the task to check
 * @param state the iElements state
 * @returns true if the task should be shown in the progress overview
 */
function shouldShowTask(
  task: BackgroundTask,
  state: LightweightElementsStore,
): boolean {
  if (isCreateProjectSnapshotTask(task) || isVlmIndexMigrationTask(task)) {
    return true;
  }

  if (
    isFileUploadTask(task) ||
    isProcessingTask(task) ||
    isTiledImageSheetGenerationTask(task) ||
    isAssetDetectionTask(task) ||
    isMultiFileUploadTask(task) ||
    isReportGenerationTask(task)
  ) {
    return isTaskVisible(task);
  }

  // Tasks that we only want to show, if the element wasn't deleted yet.
  if (
    isCaptur3dFloorplanGenerationTask(task) ||
    isGenericRegistrationTask(task) ||
    isOrthophotoGenerationTask(task) ||
    isPointCloudExportTask(task)
  ) {
    return isValidTaskWithLightweightElement(task, state);
  }

  return false;
}

/**
 * @param task the task to check
 * @returns true if it makes sense to show this task in the cloud activity menu
 */
export function isTaskVisible(task: BackgroundTask): boolean {
  // The silent upload tasks should not be visible.
  if (isFileUploadTask(task) && task.silent) {
    return false;
  }

  if (isMultiFileUploadTask(task) && task.silent) {
    return false;
  }

  const isCadUpload =
    !!task.metadata &&
    task.metadata.uploadElementType === UploadElementType.cad;

  const isUnplaced2DPhotoUpload =
    !!task.metadata &&
    task.metadata.uploadElementType === UploadElementType.unplaced2dPhotos;

  // CAD upload task, scene conversion tasks and unplaced 2D photo uploads are not related to any area,
  // so they shouldn't be validated with any IElement for other uploads we do validation of IElement
  if (
    !isCadUpload &&
    !task.iElementId &&
    !isSceneConversionTask(task) &&
    !isUnplaced2DPhotoUpload &&
    !isReportGenerationTask(task)
  ) {
    return false;
  }

  if (
    !isPointCloudExportTask(task) &&
    (isBackgroundTaskActive(task.state) ||
      task.state === BackgroundTaskState.failed)
  ) {
    return true;
  }

  // Keep registration and asset detection tasks visible after success
  // so users can interact with the results (e.g., "Review results" button for asset detection)
  if (
    task.state === BackgroundTaskState.succeeded &&
    (isGenericRegistrationTask(task) ||
      isAssetDetectionTask(task) ||
      isReportGenerationTask(task))
  ) {
    return true;
  }
  return false;
}
